import { NavLink } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';
import { Logo } from './Logo';

type NavItem = {
  to: string;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

type Section = {
  title?: string;
  items: NavItem[];
};

export function Sidebar({ orgName }: { orgName: string }) {
  const sections: Section[] = [
    {
      items: [
        { to: '/', label: 'Agents', Icon: IconBot },
        { to: '/activity', label: 'Activity', Icon: IconActivity },
        { to: '/reviews', label: 'Reviews', Icon: IconReview },
      ],
    },
    {
      title: 'Settings',
      items: [
        { to: '/connectors', label: 'Connectors', Icon: IconPuzzle },
        { to: '/llm-keys', label: 'LLM keys', Icon: IconKey },
        { to: '/mcp-servers', label: 'Custom MCP servers', Icon: IconPlug },
      ],
    },
  ];

  return (
    <aside className="hidden w-64 shrink-0 border-r border-line-soft bg-surface-white md:block">
      <div className="flex h-14 items-center border-b border-line-soft px-5">
        <Logo variant="lockup" size={22} />
        <span className="ml-3 rounded bg-surface-soft px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-eyebrow text-ink-tertiary">
          Agents
        </span>
      </div>

      <div className="border-b border-line-soft px-5 py-4">
        <p className="font-mono text-[11px] font-medium uppercase tracking-eyebrow text-ink-tertiary">
          Company
        </p>
        <p className="mt-1.5 truncate text-sm font-medium text-ink-primary">{orgName}</p>
      </div>

      <nav className="px-3 py-4">
        {sections.map((section, i) => (
          <div key={i} className={i > 0 ? 'mt-6' : ''}>
            {section.title && (
              <p className="mb-2 px-3 font-mono text-[11px] font-medium uppercase tracking-eyebrow text-ink-tertiary">
                {section.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-surface-soft font-medium text-ink-primary'
                          : 'text-ink-secondary hover:bg-surface-soft/60 hover:text-ink-primary'
                      }`
                    }
                  >
                    <item.Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}

function IconActivity(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 12h4l3-8 4 16 3-8h4" />
    </svg>
  );
}
function IconPuzzle(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14 4h-4v3a2 2 0 1 1-3 2H4v4a2 2 0 1 0 2 2H4v5h4v-2a2 2 0 1 1 2-2v2h5v-3a2 2 0 1 0-2-2h2v-4h-2a2 2 0 1 1-1-3z" />
    </svg>
  );
}
function IconKey(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="8" cy="14" r="4" />
      <path d="M11 14l9-9M16 7l3 3" />
    </svg>
  );
}
function IconBot(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <path d="M12 4v4" />
      <circle cx="9" cy="14" r="1" />
      <circle cx="15" cy="14" r="1" />
      <path d="M9 18h6" />
    </svg>
  );
}
function IconPlug(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M9 2v6M15 2v6" />
      <rect x="7" y="8" width="10" height="6" rx="1.5" />
      <path d="M12 14v4a4 4 0 0 1-4 4" />
    </svg>
  );
}
function IconReview(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M9 11l3 3 7-7" />
      <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
    </svg>
  );
}
